/**
 * ============================================================
 * KEJJA OS — src/pages/admin/AdminAdminsPage.tsx
 * Manage super admins — list + add by email + revoke.
 * ============================================================
 */

import { useState, type FormEvent } from 'react';
import { Shield, ShieldOff, UserPlus, AlertCircle, Loader2, Check } from 'lucide-react';
import { useAdminList, useGrantAdmin, useSetSuperAdmin, useAdminSession } from '@/hooks/useAdmin';

export default function AdminAdminsPage() {
  const { data, isLoading } = useAdminList();
  const { data: currentAdmin } = useAdminSession();
  const grantMut = useGrantAdmin();
  const setSuperMut = useSetSuperAdmin();

  const [email, setEmail] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  const admins = data?.admins ?? [];

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setErr(null); setOk(null);
    if (!email.trim()) return;
    grantMut.mutate(email.trim(), {
      onSuccess: (r) => { setOk(`Granted super admin to ${r.user.email}`); setEmail(''); },
      onError:   (e) => setErr(e.message || 'Failed to grant'),
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-bold">Admin users</h1>
        <p className="text-xs text-slate-400 mt-0.5">Super admins have full system access. The user must already have a Kejja account.</p>
      </div>

      {/* Grant form */}
      <section>
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-4">
          <div className="flex items-center gap-1.5 mb-3">
            <UserPlus className="w-4 h-4 text-sky-400" />
            <div className="text-sm font-semibold">Grant super admin</div>
          </div>
          <form onSubmit={submit} className="flex gap-2">
            <input
              type="email" required placeholder="user@example.com"
              value={email} onChange={(e) => setEmail(e.target.value)}
              className="flex-1 h-9 px-3 rounded-md bg-slate-950 border border-slate-700 text-white placeholder-slate-500 text-sm focus:border-sky-500 focus:ring-1 focus:ring-sky-500 outline-none"
            />
            <button type="submit" disabled={grantMut.isPending || !email.trim()}
              className="h-9 px-4 bg-sky-600 hover:bg-sky-700 disabled:opacity-50 rounded-md text-sm font-semibold flex items-center gap-1.5">
              {grantMut.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Shield className="w-3.5 h-3.5" />}
              Grant
            </button>
          </form>
          {err && (
            <div className="mt-2 flex items-start gap-1.5 text-rose-400 text-xs">
              <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" /> {err}
            </div>
          )}
          {ok && (
            <div className="mt-2 flex items-start gap-1.5 text-emerald-400 text-xs">
              <Check className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" /> {ok}
            </div>
          )}
        </div>
      </section>

      {/* Current admins list */}
      <section>
        <h2 className="text-sm font-semibold text-slate-300 mb-2">Current super admins ({admins.length})</h2>
        {isLoading ? (
          <div className="text-slate-400 text-xs">Loading…</div>
        ) : (
          <div className="bg-slate-900 border border-slate-800 rounded-lg divide-y divide-slate-800">
            {admins.map((a) => {
              const isCurrent = a.id === currentAdmin?.id;
              return (
                <div key={a.id} className="p-3 flex items-center gap-3">
                  <div className="h-9 w-9 rounded-full bg-rose-600/15 border border-rose-600/30 text-rose-400 flex items-center justify-center text-[11px] font-bold flex-shrink-0">
                    {(a.displayName || a.email).slice(0, 2).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <div className="text-sm font-medium text-slate-100 truncate">{a.displayName}</div>
                      {isCurrent && <span className="text-[10px] px-1.5 py-0.5 rounded bg-sky-500/15 text-sky-400 font-semibold uppercase">You</span>}
                    </div>
                    <div className="text-xs text-slate-500 truncate">{a.email}</div>
                  </div>
                  <div className="text-[11px] text-slate-400 whitespace-nowrap hidden sm:block">
                    {a.lastLoginAt ? `Last login: ${new Date(a.lastLoginAt).toLocaleDateString('en-KE', { month: 'short', day: 'numeric' })}` : 'Never'}
                  </div>
                  <button
                    onClick={() => {
                      if (isCurrent) { alert('You cannot revoke your own admin rights.'); return; }
                      if (confirm(`Revoke super admin from ${a.email}?`)) {
                        setSuperMut.mutate({ userId: a.id, value: false });
                      }
                    }}
                    disabled={isCurrent}
                    className="p-1.5 rounded hover:bg-slate-800 text-slate-400 hover:text-rose-400 disabled:opacity-30 disabled:cursor-not-allowed"
                    title={isCurrent ? 'Cannot revoke yourself' : 'Revoke super admin'}
                  >
                    <ShieldOff className="w-4 h-4" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
